/**
 * SpellSystem.ts - Spell casting mechanics and calculations
 * Handles spell effects, damage, healing, and status effects
 */

import {
  SpellDefinition,
  Element,
  SpellTarget,
  StatusEffect,
  CharacterClass,
  getSpell,
} from './data/SpellData';

// ============================================================================
// TYPES
// ============================================================================

export interface Character {
  id: number;
  name: string;
  class: CharacterClass;
  level: number;
  hp: number;
  maxHP: number;
  mp: number;
  maxMP: number;
  intelligence: number; // INT stat for spell power
  spirit: number; // For healing/resistance
  status: StatusEffect[];
  spellCharges: Map<number, number>; // spellId -> remaining charges
  elementalResistances: Map<Element, number>; // 0-100 resistance %
}

export interface Enemy {
  id: number;
  name: string;
  hp: number;
  maxHP: number;
  magicDefense: number;
  elementalWeaknesses: Map<Element, number>; // negative = weak, positive = resist
  isUndead: boolean;
  status: StatusEffect[];
  dead: boolean;
}

export interface SpellResult {
  success: boolean;
  hit: boolean;
  damage: number;
  healing: number;
  statusApplied: StatusEffect | null;
  isCritical: boolean;
  message: string;
  targetIds: number[];
}

// ============================================================================
// SPELL SYSTEM CLASS
// ============================================================================

export class SpellSystem {
  // Elemental modifiers (FF1-accurate)
  private static readonly WEAKNESS_MULTIPLIER = 2.0;
  private static readonly RESISTANCE_MULTIPLIER = 0.5;
  private static readonly ABSORBED_MULTIPLIER = -1.0;

  // Variance for damage/healing
  private static readonly MIN_VARIANCE = 0.875; // 87.5%
  private static readonly MAX_VARIANCE = 1.125; // 112.5%

  /**
   * Cast a spell from caster to targets
   */
  static castSpell(
    spellId: number,
    caster: Character,
    targets: (Character | Enemy)[],
    partyMembers: Character[],
    enemies: Enemy[]
  ): SpellResult {
    const spell = getSpell(spellId);

    if (!spell) {
      return {
        success: false,
        hit: false,
        damage: 0,
        healing: 0,
        statusApplied: null,
        isCritical: false,
        message: 'Invalid spell!',
        targetIds: [],
      };
    }

    // Check if caster has charges
    const charges = caster.spellCharges.get(spellId) || 0;
    if (charges <= 0) {
      return {
        success: false,
        hit: false,
        damage: 0,
        healing: 0,
        statusApplied: null,
        isCritical: false,
        message: 'No charges left!',
        targetIds: [],
      };
    }

    // Deduct charge
    caster.spellCharges.set(spellId, charges - 1);

    // Check if silenced
    if (caster.status.includes(StatusEffect.Silence)) {
      return {
        success: false,
        hit: false,
        damage: 0,
        healing: 0,
        statusApplied: null,
        isCritical: false,
        message: `${caster.name} is silenced!`,
        targetIds: [],
      };
    }

    // Determine actual targets based on spell target type
    const actualTargets = this.getActualTargets(spell, targets, partyMembers, enemies);

    // Calculate spell effects
    return this.applySpellEffect(spell, caster, actualTargets);
  }

  /**
   * Get actual targets based on spell targeting
   */
  private static getActualTargets(
    spell: SpellDefinition,
    selectedTargets: (Character | Enemy)[],
    partyMembers: Character[],
    enemies: Enemy[]
  ): (Character | Enemy)[] {
    switch (spell.target) {
      case SpellTarget.Self:
        return [selectedTargets[0]]; // Caster

      case SpellTarget.SingleAlly:
        return [selectedTargets[0]];

      case SpellTarget.AllAllies:
        return partyMembers.filter((p) => p.hp > 0 || spell.statusEffect === StatusEffect.Dead);

      case SpellTarget.SingleEnemy:
        return [selectedTargets[0]];

      case SpellTarget.AllEnemies:
        return enemies.filter((e) => !e.dead);

      default:
        return selectedTargets;
    }
  }

  /**
   * Apply spell effect to targets
   */
  private static applySpellEffect(
    spell: SpellDefinition,
    caster: Character,
    targets: (Character | Enemy)[]
  ): SpellResult {
    let totalDamage = 0;
    let totalHealing = 0;
    let statusApplied: StatusEffect | null = null;
    let hit = false;
    const targetIds: number[] = [];

    for (const target of targets) {
      targetIds.push(target.id);

      // Roll hit chance
      const hitRoll = Math.random() * 100;
      if (hitRoll > spell.accuracy) {
        continue; // Miss
      }

      hit = true;

      if (spell.isHealing) {
        // Healing spell
        const healing = this.calculateHealing(spell, caster, target as Character);
        totalHealing += healing;

        if ('hp' in target) {
          const char = target as Character;
          char.hp = Math.min(char.maxHP, char.hp + healing);
        }

        // Handle resurrection
        if (spell.statusEffect === StatusEffect.Dead && 'status' in target) {
          const char = target as Character;
          char.status = char.status.filter((s) => s !== StatusEffect.Dead);
        }
      } else {
        // Damage or status spell
        if (spell.power > 0) {
          const damage = this.calculateDamage(spell, caster, target);
          totalDamage += damage;

          if ('hp' in target) {
            target.hp = Math.max(0, target.hp - damage);

            // Check death
            if (target.hp <= 0) {
              if ('dead' in target) {
                (target as Enemy).dead = true;
              } else {
                (target as Character).status.push(StatusEffect.Dead);
              }
            }
          }
        }

        // Apply status effect
        if (spell.statusEffect !== StatusEffect.None) {
          const statusRoll = Math.random() * 100;
          if (statusRoll <= spell.statusChance) {
            statusApplied = spell.statusEffect;

            if ('status' in target) {
              if (target instanceof Object && 'status' in target) {
                const statusList = (target as any).status as StatusEffect[];
                if (!statusList.includes(spell.statusEffect)) {
                  statusList.push(spell.statusEffect);
                }
              }
            }
          }
        }
      }
    }

    return {
      success: true,
      hit,
      damage: totalDamage,
      healing: totalHealing,
      statusApplied,
      isCritical: false, // Spells don't crit in FF1
      message: this.getSpellMessage(spell, hit, totalDamage, totalHealing, statusApplied),
      targetIds,
    };
  }

  /**
   * Calculate spell damage (FF1 formula)
   */
  private static calculateDamage(
    spell: SpellDefinition,
    caster: Character,
    target: Character | Enemy
  ): number {
    // Base damage = Power + (Intelligence / 2)
    let baseDamage = spell.power + Math.floor(caster.intelligence / 2);

    // Add variance
    const variance = this.MIN_VARIANCE + Math.random() * (this.MAX_VARIANCE - this.MIN_VARIANCE);
    baseDamage = Math.floor(baseDamage * variance);

    // Apply magic defense (only for enemies)
    if ('magicDefense' in target) {
      const enemy = target as Enemy;
      const defenseReduction = Math.floor(baseDamage * (enemy.magicDefense / 256));
      baseDamage = Math.max(1, baseDamage - defenseReduction);
    }

    // Apply elemental modifiers
    baseDamage = this.applyElementalModifier(baseDamage, spell.element, target);

    // Holy damage bonus vs undead
    if (spell.element === Element.Holy && 'isUndead' in target && target.isUndead) {
      baseDamage = Math.floor(baseDamage * 1.5);
    }

    return Math.max(1, Math.floor(baseDamage));
  }

  /**
   * Calculate healing amount (FF1 formula)
   */
  private static calculateHealing(
    spell: SpellDefinition,
    caster: Character,
    target: Character
  ): number {
    // Base healing = Power + (Spirit / 2)
    let baseHealing = spell.power + Math.floor(caster.spirit / 2);

    // Add variance
    const variance = this.MIN_VARIANCE + Math.random() * (this.MAX_VARIANCE - this.MIN_VARIANCE);
    baseHealing = Math.floor(baseHealing * variance);

    return Math.max(1, Math.floor(baseHealing));
  }

  /**
   * Apply elemental modifier based on weaknesses/resistances
   */
  private static applyElementalModifier(
    damage: number,
    element: Element,
    target: Character | Enemy
  ): number {
    if (element === Element.None || element === Element.Status) {
      return damage;
    }

    // Check character resistances
    if ('elementalResistances' in target) {
      const resistance = (target as Character).elementalResistances.get(element) || 0;
      if (resistance > 0) {
        damage = Math.floor(damage * (1 - resistance / 100));
      }
    }

    // Check enemy weaknesses
    if ('elementalWeaknesses' in target) {
      const weakness = (target as Enemy).elementalWeaknesses.get(element) || 0;
      if (weakness < 0) {
        // Weak to element
        damage = Math.floor(damage * this.WEAKNESS_MULTIPLIER);
      } else if (weakness > 0) {
        // Resistant to element
        damage = Math.floor(damage * this.RESISTANCE_MULTIPLIER);
      }
    }

    return damage;
  }

  /**
   * Get spell result message
   */
  private static getSpellMessage(
    spell: SpellDefinition,
    hit: boolean,
    damage: number,
    healing: number,
    statusApplied: StatusEffect | null
  ): string {
    if (!hit) {
      return `${spell.name} missed!`;
    }

    if (healing > 0) {
      return `${spell.name} restored ${healing} HP!`;
    }

    if (damage > 0) {
      return `${spell.name} dealt ${damage} damage!`;
    }

    if (statusApplied) {
      return `${spell.name} inflicted ${StatusEffect[statusApplied]}!`;
    }

    return `${spell.name} had no effect.`;
  }

  /**
   * Initialize spell charges for a character
   */
  static initializeSpellCharges(character: Character, learnedSpells: number[]): void {
    character.spellCharges = new Map();

    for (const spellId of learnedSpells) {
      const spell = getSpell(spellId);
      if (spell) {
        const charges = spell.chargesPerLevel * spell.level;
        character.spellCharges.set(spellId, charges);
      }
    }
  }

  /**
   * Restore spell charges (e.g., at an inn)
   */
  static restoreSpellCharges(character: Character, learnedSpells: number[]): void {
    for (const spellId of learnedSpells) {
      const spell = getSpell(spellId);
      if (spell) {
        const maxCharges = spell.chargesPerLevel * spell.level;
        character.spellCharges.set(spellId, maxCharges);
      }
    }
  }

  /**
   * Get remaining charges for a spell
   */
  static getRemainingCharges(character: Character, spellId: number): number {
    return character.spellCharges.get(spellId) || 0;
  }

  /**
   * Get max charges for a spell
   * Base max charges decrease by spell level (higher level = fewer max charges)
   * Level 1: 3 max, Level 2: 3 max, Level 3: 2 max, etc.
   */
  static getMaxCharges(spellId: number): number {
    const spell = getSpell(spellId);
    if (!spell) return 0;
    // Base 3 charges for low level spells, reducing at higher levels
    if (spell.level <= 2) return 3;
    if (spell.level <= 4) return 2;
    return 1; // High level spells (5-8) get only 1 charge
  }

  /**
   * Check if character can cast spell
   */
  static canCastSpell(character: Character, spellId: number): boolean {
    // Check silence
    if (character.status.includes(StatusEffect.Silence)) {
      return false;
    }

    // Check charges
    const charges = character.spellCharges.get(spellId) || 0;
    return charges > 0;
  }

  /**
   * Get spell list for character organized by level
   */
  static getSpellsByLevel(
    _character: Character,
    learnedSpells: number[]
  ): Map<number, SpellDefinition[]> {
    const spellsByLevel = new Map<number, SpellDefinition[]>();

    for (const spellId of learnedSpells) {
      const spell = getSpell(spellId);
      if (spell) {
        if (!spellsByLevel.has(spell.level)) {
          spellsByLevel.set(spell.level, []);
        }
        spellsByLevel.get(spell.level)!.push(spell);
      }
    }

    return spellsByLevel;
  }

  /**
   * Create spell visual effect data
   */
  static getSpellEffect(spellId: number): SpellEffectData {
    const spell = getSpell(spellId);
    if (!spell) {
      return {
        color: '#ffffff',
        duration: 500,
        type: 'flash',
        particles: 0,
      };
    }

    // Different effects based on element
    switch (spell.element) {
      case Element.Fire:
        return {
          color: '#ff4400',
          duration: 600,
          type: 'explosion',
          particles: 20,
        };

      case Element.Ice:
        return {
          color: '#44aaff',
          duration: 700,
          type: 'shards',
          particles: 15,
        };

      case Element.Lightning:
        return {
          color: '#ffff00',
          duration: 400,
          type: 'bolt',
          particles: 10,
        };

      case Element.Holy:
        return {
          color: '#ffffff',
          duration: 800,
          type: 'glow',
          particles: 30,
        };

      case Element.Dark:
        return {
          color: '#440088',
          duration: 600,
          type: 'swirl',
          particles: 25,
        };

      default:
        return {
          color: '#ffffff',
          duration: 500,
          type: 'flash',
          particles: 0,
        };
    }
  }
}

// ============================================================================
// SPELL EFFECT DATA
// ============================================================================

export interface SpellEffectData {
  color: string;
  duration: number; // milliseconds
  type: 'flash' | 'explosion' | 'shards' | 'bolt' | 'glow' | 'swirl';
  particles: number;
}
